import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../contexts/AuthContext';
import { useSessionState } from '../hooks/useSessionState';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Cpu, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ITRequestTypesPage() {
  const { getAuthHeader, employee } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useSessionState('it-request-types:search', '');
  const [filterCategory, setFilterCategory] = useSessionState('it-request-types:cat', '');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ category: '', subcategory: '', sla_hours: 4, is_active: true });

  const isSuperAdmin = employee?.role === 'Super Admin';

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = isSuperAdmin ? `${API}/it-request-types/all` : `${API}/it-request-types`;
      const res = await fetch(endpoint, { headers: getAuthHeader() });
      if (res.ok) setItems(await res.json());
    } catch (e) {
      toast.error('Failed to load IT request types');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, isSuperAdmin]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const categories = useMemo(() => Array.from(new Set(items.map(i => i.category))).sort(), [items]);
  const filtered = useMemo(() => items.filter(i => {
    if (filterCategory && i.category !== filterCategory) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!i.category.toLowerCase().includes(q) && !i.subcategory.toLowerCase().includes(q)) return false;
    }
    return true;
  }), [items, searchQuery, filterCategory]);

  const openCreate = () => {
    setEditing(null);
    setForm({ category: '', subcategory: '', sla_hours: 4, is_active: true });
    setDialogOpen(true);
  };
  const openEdit = (item) => {
    setEditing(item);
    setForm({ category: item.category, subcategory: item.subcategory, sla_hours: item.sla_hours || 4, is_active: !!item.is_active });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.category.trim() || !form.subcategory.trim()) {
      toast.error('Category and Subcategory are required');
      return;
    }
    try {
      const url = editing ? `${API}/it-request-types/${editing.id}` : `${API}/it-request-types`;
      const method = editing ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category: form.category.trim(),
          subcategory: form.subcategory.trim(),
          sla_hours: parseInt(form.sla_hours, 10) || 4,
          is_active: form.is_active,
        }),
      });
      if (res.ok) {
        toast.success(editing ? 'Updated' : 'Created');
        setDialogOpen(false);
        fetchItems();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save');
      }
    } catch (e) {
      toast.error('Failed to save');
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Deactivate "${item.category} → ${item.subcategory}"?`)) return;
    try {
      const res = await fetch(`${API}/it-request-types/${item.id}`, { method: 'DELETE', headers: getAuthHeader() });
      if (res.ok) {
        toast.success('Deactivated');
        fetchItems();
      }
    } catch (e) {
      toast.error('Failed to deactivate');
    }
  };

  return (
    <Layout>
      <div className="space-y-5" data-testid="it-request-types-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <Cpu className="w-8 h-8 text-emerald-600" /> IT Request Types
            </h1>
            <p className="text-muted-foreground mt-1">Manage IT category, subcategory and SLA hours</p>
          </div>
          {isSuperAdmin && (
            <Button onClick={openCreate} data-testid="add-it-request-type-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Request Type
            </Button>
          )}
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">All Types ({filtered.length} of {items.length})</CardTitle>
            <CardDescription>{categories.length} categories</CardDescription>
            <div className="flex flex-wrap gap-2 mt-2">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search category or subcategory..."
                  className="pl-9 h-9"
                  data-testid="it-rt-search"
                />
              </div>
              <select
                value={filterCategory}
                onChange={e => setFilterCategory(e.target.value)}
                className="border rounded-md px-3 h-9 text-sm bg-background"
                data-testid="it-rt-category-filter"
              >
                <option value="">All Categories</option>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              {isSuperAdmin && filterCategory && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const next = window.prompt(`Rename category "${filterCategory}" to:`, filterCategory);
                      if (!next || !next.trim() || next.trim() === filterCategory) return;
                      fetch(`${API}/it-request-types/category/rename`, {
                        method: 'PUT',
                        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
                        body: JSON.stringify({ old_name: filterCategory, new_name: next.trim() }),
                      })
                        .then(r => r.json().then(j => ({ ok: r.ok, j })))
                        .then(({ ok, j }) => {
                          if (ok) {
                            toast.success(`Renamed (${j.updated} subcategories updated)`);
                            setFilterCategory(next.trim());
                            fetchItems();
                          } else {
                            toast.error(j.detail || 'Rename failed');
                          }
                        })
                        .catch(() => toast.error('Network error'));
                    }}
                    data-testid="it-rt-rename-category-btn"
                  >
                    Rename Category
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => {
                      if (!window.confirm(`Delete entire category "${filterCategory}"? All its subcategories will be deactivated.`)) return;
                      fetch(`${API}/it-request-types/category/${encodeURIComponent(filterCategory)}`, {
                        method: 'DELETE',
                        headers: getAuthHeader(),
                      })
                        .then(r => r.json().then(j => ({ ok: r.ok, j })))
                        .then(({ ok, j }) => {
                          if (ok) {
                            toast.success(`Deactivated ${j.deactivated} subcategories`);
                            setFilterCategory('');
                            fetchItems();
                          } else {
                            toast.error(j.detail || 'Delete failed');
                          }
                        })
                        .catch(() => toast.error('Network error'));
                    }}
                    data-testid="it-rt-delete-category-btn"
                  >
                    Delete Category
                  </Button>
                </>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center py-8 text-muted-foreground">Loading...</p>
            ) : (
              <div className="border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead>Category</TableHead>
                      <TableHead>Subcategory</TableHead>
                      <TableHead className="w-24 text-center">SLA (hrs)</TableHead>
                      <TableHead className="w-24 text-center">Active</TableHead>
                      {isSuperAdmin && <TableHead className="w-32 text-right">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map(item => (
                      <TableRow key={item.id} data-testid={`it-rt-row-${item.id}`}>
                        <TableCell className="font-medium">{item.category}</TableCell>
                        <TableCell>{item.subcategory}</TableCell>
                        <TableCell className="text-center">{item.sla_hours || 4}</TableCell>
                        <TableCell className="text-center">
                          {item.is_active ? (
                            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Active</Badge>
                          ) : (
                            <Badge variant="outline" className="text-muted-foreground">Inactive</Badge>
                          )}
                        </TableCell>
                        {isSuperAdmin && (
                          <TableCell className="text-right">
                            <Button size="sm" variant="ghost" onClick={() => openEdit(item)} data-testid={`it-rt-edit-${item.id}`}>
                              <Edit className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => remove(item)} data-testid={`it-rt-delete-${item.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit IT Request Type' : 'Add IT Request Type'}</DialogTitle>
              <DialogDescription>Configure category, subcategory and SLA hours</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="space-y-1">
                <Label>Category *</Label>
                <Input
                  value={form.category}
                  onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
                  placeholder="e.g., Hardware, Software, Network"
                  data-testid="it-rt-form-category"
                  list="it-rt-cat-list"
                />
                <datalist id="it-rt-cat-list">
                  {categories.map(c => <option key={c} value={c} />)}
                </datalist>
              </div>
              <div className="space-y-1">
                <Label>Subcategory *</Label>
                <Input
                  value={form.subcategory}
                  onChange={e => setForm(p => ({ ...p, subcategory: e.target.value }))}
                  placeholder="e.g., Laptop / Desktop issue"
                  data-testid="it-rt-form-subcategory"
                />
              </div>
              <div className="space-y-1">
                <Label>SLA (working hours)</Label>
                <Input
                  type="number"
                  min="1"
                  max="40"
                  value={form.sla_hours}
                  onChange={e => setForm(p => ({ ...p, sla_hours: e.target.value }))}
                  data-testid="it-rt-form-sla"
                />
                <p className="text-xs text-muted-foreground">Calculated within Mon–Fri 8 AM – 7 PM working hours.</p>
              </div>
              <div className="flex items-center justify-between">
                <Label>Active</Label>
                <Switch
                  checked={form.is_active}
                  onCheckedChange={(v) => setForm(p => ({ ...p, is_active: v }))}
                  data-testid="it-rt-form-active"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={save} data-testid="it-rt-save-btn">{editing ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
